'use client';

import React, { useEffect, useRef } from 'react';
import type { Editor } from 'grapesjs';

interface GrapesJSLayersPanelProps {
  editor: Editor | null;
}

const GrapesJSLayersPanel: React.FC<GrapesJSLayersPanelProps> = ({ editor }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    if (!editor || !containerRef.current) return;
    
    // Clear the container
    containerRef.current.innerHTML = '';
    
    try {
      // Get the layer manager
      const layerManager = editor.Layers;
      
      // Render the layers view directly into our container
      // This uses GrapesJS's built-in layers rendering
      const layersEl = layerManager.render();
      
      // Append the layers element to our container
      containerRef.current.appendChild(layersEl);
      
      // Add custom styling for the layers panel
      addLayersPanelStyling();
      
    } catch (error) {
      console.error("Error rendering layers panel:", error);
      if (containerRef.current) {
        containerRef.current.innerHTML = 
          `<div class="flex items-center justify-center h-full text-red-400">
            Error loading layers panel: ${error}
          </div>`;
      }
    }
    
    // Clean up on unmount
    return () => {
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
      // Remove custom styling
      const styleEl = document.getElementById('gjs-layers-custom-style');
      if (styleEl) styleEl.remove();
    };
  }, [editor]);
  
  // Function to add custom styling for the layers panel
  const addLayersPanelStyling = () => {
    // Remove any existing styling first
    const existingStyle = document.getElementById('gjs-layers-custom-style');
    if (existingStyle) existingStyle.remove();
    
    // Create style element
    const styleEl = document.createElement('style');
    styleEl.id = 'gjs-layers-custom-style';
    
    // Set the CSS styles to match the design in the image
    styleEl.innerHTML = `
      /* Main container styling */
      .gjs-layers {
        background-color: #1E1E1E;
        color: white;
        font-family: sans-serif;
        padding: 5px 0;
      }
      
      /* Layer item styling */
      .gjs-layer {
        position: relative;
        border-bottom: 1px solid rgba(255, 255, 255, 0.07);
        transition: all 0.2s ease;
      }
      
      /* Layer item container */
      .gjs-layer-title-c {
        background-color: #2d2d2d !important;
      }
      
      /* Layer title */
      .gjs-layer-title {
        padding: 10px 0;
        font-size: 12px;
        color: #ddd;
      }
      
      /* Layer name */
      .gjs-layer-name {
        color: #ddd;
        font-size: 12px;
      }
      
      /* Layer count */
      .gjs-layer-count {
        color: rgba(255, 255, 255, 0.7);
        font-size: 10px;
        margin-right: 10px;
      }
      
      /* Move icon */
      .gjs-layer-move {
        cursor: move;
        display: flex;
        align-items: center;
        padding-right: 10px;
      }
      
      .gjs-layer-move svg {
        width: 16px;
        height: 16px;
        color: rgba(255, 255, 255, 0.5);
      }
      
      /* Visibility toggle */
      .gjs-layer-vis {
        padding-left: 10px;
        cursor: pointer;
        display: flex;
      }
      
      .gjs-layer-vis svg {
        width: 16px;
        height: 16px;
        color: rgba(255, 255, 255, 0.8);
      }
      
      /* Caret for expandable items */
      .gjs-layer-caret {
        width: 16px;
        height: 16px;
        margin-right: 5px;
        display: inline-flex;
        align-items: center;
        cursor: pointer;
        color: rgba(255, 255, 255, 0.7);
      }
      
      /* Open caret styling */
      .gjs-layer-caret.gjs-layer-open {
        transform: rotate(180deg);
      }
      
      /* Hidden elements */
      .gjs-layer.gjs-hidden {
        opacity: 0.5;
      }
      
      /* Selected layer */
      .gjs-layer.gjs-selected .gjs-layer-title-c {
        background-color: #3a3a3a !important;
      }
      
      /* Improve layering with dark colors */
      .gjs-one-bg {
        background-color: #2d2d2d !important;
      }
      
      .gjs-two-color {
        color: #ddd !important;
      }
      
      /* Make the icons in the layer panel look better */
      .gjs-layer svg {
        width: 16px;
        height: 16px;
      }
    `;
    
    // Add style to document
    document.head.appendChild(styleEl);
  };
  
  return (
    <div 
      ref={containerRef} 
      className="h-full overflow-auto bg-[#1E1E1E] text-white p-0"
    >
      {!editor && (
        <div className="flex items-center justify-center h-full text-gray-400">
          Editor not initialized
        </div>
      )}
    </div>
  );
};

export default GrapesJSLayersPanel;